// NavDrawer — the header's navigation on a phone.
//
// At phone width the menubar has no room and the tab bar is gone, so both
// move in here: the same entries TopNav draws as dropdowns (it hands them
// over already filtered — one list, two presentations), the open pages the
// tab bar would have shown, and the two switches that lived on the tab bar
// (theme, admin mode).
//
// Nothing here hovers or flies out: a menu opens in place, one level
// indented per depth, because a finger cannot hover and a fly-out has
// nowhere to fly to on a screen this wide.
//
// Portalled to <body>: the header is its own stacking context (and must
// never be transformed — see TopNav.jsx), and the drawer has to sit over
// the page, not inside the bar.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { useTab } from '@platform/app-shell/frontend/contexts/TabContext';
import { useTheme } from '@platform/app-shell/frontend/contexts/ThemeContext';
import { LanguageRows } from '@platform/translator/frontend/LanguageButton';
import { isAdminUser } from '@platform/app-shell/frontend/utils/adminAuth';
import { resolveNavUrl, isExternalNavUrl } from '@platform/app-shell/frontend/utils/navUrls';

const resolveUrl = (rawUrl) => resolveNavUrl(rawUrl, window.location.origin);

const Chevron = ({ open }) => (
  <svg className={`tn-drawer-chevron ${open ? 'tn-drawer-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 10 10" aria-hidden="true">
    <path d="M3.5 1 L7 5 L3.5 9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// A menu's tree, opened in place. `renderIcon` and `ExternalMark` come from
// TopNav so an item looks the same here as in its dropdown.
function DrawerTree({ items, depth, onNavigate, renderIcon, ExternalMark }) {
  const [openIds, setOpenIds] = useState(() => new Set());
  const toggle = (key) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  return (
    <ul className="tn-drawer-list" role="list">
      {items.map((item, index) => {
        const key = item.id || `${depth}-${index}`;
        const hasChildren = item.children && item.children.length > 0;
        const url = hasChildren ? null : resolveUrl(item.url);
        const isOpen = openIds.has(key);
        return (
          <li key={key}>
            <button
              type="button"
              className="tn-drawer-row"
              style={{ paddingLeft: 16 + depth * 16 }}
              aria-expanded={hasChildren ? isOpen : undefined}
              disabled={!hasChildren && !url}
              onClick={() => (hasChildren ? toggle(key) : onNavigate(url, item.label))}
            >
              {renderIcon(item, null)}
              <span className="tn-drawer-row-label">{item.label}</span>
              {url && isExternalNavUrl(url) && <ExternalMark />}
              {hasChildren && <Chevron open={isOpen} />}
            </button>
            {hasChildren && isOpen && (
              <DrawerTree items={item.children} depth={depth + 1} onNavigate={onNavigate} renderIcon={renderIcon} ExternalMark={ExternalMark} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function NavDrawer({ open, onClose, entries, onNavigate, title, renderIcon, ExternalMark }) {
  const { userGroups } = useAuth();
  const { tabs, activeTabId, switchTab, closeTab, adminMode, toggleAdminMode } = useTab();
  const { isDark, toggleTheme } = useTheme();
  const [openMenus, setOpenMenus] = useState(() => new Set());
  const closeRef = useRef(null);

  // Escape closes; focus goes to the close button on the way in and back to
  // whatever opened the drawer on the way out.
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const go = (url, label) => { if (!url) return; onClose(); onNavigate(url, label); };
  const toggleMenu = (id) => setOpenMenus((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return createPortal(
    <div className="tn-drawer-scrim" onClick={onClose}>
      <aside
        className="tn-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tn-drawer-head">
          <span className="tn-drawer-title">{title}</span>
          <button type="button" className="tn-drawer-close" ref={closeRef} onClick={onClose} aria-label="Close navigation">×</button>
        </div>

        <div className="tn-drawer-body">
          <nav aria-label="Main navigation">
            <ul className="tn-drawer-list" role="list">
              {entries.map((entry) => {
                if (entry.kind === 'link') {
                  return (
                    <li key={entry.id}>
                      <button type="button" className="tn-drawer-row tn-drawer-row--top" disabled={!entry.url} onClick={() => go(entry.url, entry.tabLabel)}>
                        {renderIcon(entry.item, entry.iconFallback)}
                        <span className="tn-drawer-row-label">{entry.label}</span>
                        {entry.external && <ExternalMark />}
                      </button>
                    </li>
                  );
                }
                const isOpen = openMenus.has(entry.id);
                return (
                  <li key={entry.id}>
                    <button type="button" className="tn-drawer-row tn-drawer-row--top" aria-expanded={isOpen} onClick={() => toggleMenu(entry.id)}>
                      {renderIcon(entry.item, entry.iconFallback)}
                      <span className="tn-drawer-row-label">{entry.label}</span>
                      <Chevron open={isOpen} />
                    </button>
                    {isOpen && (entry.items.length === 0
                      ? <div className="tn-drawer-empty">{entry.emptyText}</div>
                      : <DrawerTree items={entry.items} depth={1} onNavigate={go} renderIcon={renderIcon} ExternalMark={ExternalMark} />)}
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="tn-drawer-section">Open pages</div>
          <ul className="tn-drawer-list" role="list">
            {tabs.map((tab) => (
              <li key={tab.id} className="tn-drawer-tab">
                <button
                  type="button"
                  className={`tn-drawer-row ${tab.id === activeTabId ? 'tn-drawer-row--current' : ''}`}
                  aria-current={tab.id === activeTabId ? 'page' : undefined}
                  onClick={() => { switchTab(tab.id); onClose(); }}
                >
                  <span className="tn-drawer-row-label">{tab.title}</span>
                </button>
                <button type="button" className="tn-drawer-tab-close" onClick={() => closeTab(tab.id)} aria-label={`Close ${tab.title}`}>×</button>
              </li>
            ))}
          </ul>
        </div>

        <div className="tn-drawer-foot">
          <button type="button" className="tn-drawer-row" onClick={toggleTheme}>
            <span className="tn-drawer-row-label">{isDark ? 'Switch to light mode' : 'Switch to dark mode'}</span>
          </button>
          {/* the Translator's languages — nothing unless a site admin switched it on */}
          <LanguageRows />
          {isAdminUser(userGroups) && (
            <button type="button" className="tn-drawer-row" aria-pressed={adminMode} onClick={toggleAdminMode}>
              <span className="tn-drawer-row-label">{adminMode ? 'Exit admin mode' : 'Enter admin mode'}</span>
            </button>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
